import { useEffect, useRef, useState } from "react";
import { Send, Sparkles, Code2, Eraser, Copy, Download, Loader2, BookmarkPlus } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { confirmDialog, promptDialog } from "@/lib/dialogs";
import { downloadBlob } from "@/lib/download";

const LANGS = ["auto", "python", "javascript", "typescript", "bash", "sql", "go", "rust", "java", "powershell"];

export default function AIPanel({ mode, project, selectedDocs, onScriptSaved }) {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [language, setLanguage] = useState("auto");
  const [sending, setSending] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    let alive = true;
    api
      .getChat(project.id, mode)
      .then((m) => {
        if (alive) setMessages(m);
      })
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, [project.id, mode]);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, sending]);

  const send = async () => {
    if (!input.trim() || sending) return;
    const userMsg = {
      id: `tmp-${Date.now()}`,
      role: "user",
      content: input,
      document_ids: selectedDocs,
      language: mode === "script" ? language : undefined,
      created_at: new Date().toISOString(),
    };
    setMessages((m) => [...m, userMsg]);
    const prompt = input;
    setInput("");
    setSending(true);
    try {
      const body = { project_id: project.id, prompt, document_ids: selectedDocs };
      const res = mode === "review"
        ? await api.aiReview(body)
        : await api.aiScript({ ...body, language });
      setMessages((m) => [
        ...m,
        {
          id: res.message_id,
          role: "assistant",
          content: res.answer,
          document_ids: selectedDocs,
          language: mode === "script" ? language : undefined,
          created_at: new Date().toISOString(),
        },
      ]);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "AI request failed");
      setMessages((m) => m.slice(0, -1));
      setInput(prompt);
    } finally {
      setSending(false);
    }
  };

  const clear = async () => {
    const ok = await confirmDialog({
      title: "Clear thread?",
      message: "All messages in this AI thread will be deleted.",
      confirmLabel: "Clear",
      danger: true,
    });
    if (!ok) return;
    try {
      await api.clearChat(project.id, mode);
      setMessages([]);
      toast.success("Chat cleared");
    } catch {
      toast.error("Clear failed");
    }
  };

  const onKey = (e) => {
    if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
      e.preventDefault();
      send();
    }
  };

  const isScript = mode === "script";
  const Icon = isScript ? Code2 : Sparkles;

  return (
    <div className="flex h-full flex-col" data-testid={`ai-panel-${mode}`}>
      <div className="border-b border-[#2D7544] px-6 py-3 flex items-center gap-3">
        <Icon size={16} />
        <div className="dv-overline">{isScript ? "Script Lab" : "Document Review"}</div>
        <div className="font-mono text-[11px] text-[#66B07A] hidden md:block" data-testid="ai-context-label">
          {selectedDocs.length > 0
            ? `${selectedDocs.length} doc${selectedDocs.length === 1 ? "" : "s"} selected as context`
            : "using ALL project docs as context"}
        </div>
        <div className="ml-auto flex items-center gap-2">
          {isScript && (
            <select
              data-testid="language-select"
              value={language}
              onChange={(e) => setLanguage(e.target.value)}
              className="dv-input py-1.5 pr-7 text-xs font-mono uppercase tracking-wider"
            >
              {LANGS.map((l) => (
                <option key={l} value={l}>{l}</option>
              ))}
            </select>
          )}
          <button data-testid="clear-chat-btn" onClick={clear} className="dv-btn-ghost flex items-center gap-1">
            <Eraser size={13} /> Clear
          </button>
        </div>
      </div>

      <div ref={scrollRef} className="flex-1 overflow-y-auto px-6 py-5 flex flex-col gap-4 bg-[#000000]" data-testid="chat-scroll">
        {messages.length === 0 && !sending && (
          <div className="m-auto text-center max-w-md">
            <div className="font-heading text-3xl font-extrabold tracking-tighter">
              {isScript ? "What should we automate?" : "What should we look into?"}
            </div>
            <p className="text-sm text-[#66B07A] mt-3 leading-relaxed">
              {isScript
                ? "Describe a task — Claude will write a runnable script grounded in the documents you selected."
                : "Ask a question about the selected documents — get summaries, comparisons, risks, or extractions."}
            </p>
            <div className="font-mono text-[10px] uppercase tracking-widest text-[#3D8657] mt-4">
              ⌘ / Ctrl + Enter to send
            </div>
          </div>
        )}

        {messages.map((m, idx) => {
          const prevUser = m.role === "assistant" ? findPrevUserPrompt(messages, idx) : null;
          return (
            <MessageBubble
              key={m.id}
              m={m}
              saveContext={
                isScript && m.role === "assistant"
                  ? {
                      projectId: project.id,
                      prompt: prevUser,
                      documentIds: m.document_ids?.length ? m.document_ids : selectedDocs,
                      onSaved: () => onScriptSaved?.(),
                    }
                  : null
              }
            />
          );
        })}

        {sending && (
          <div className="self-start bg-[#050805] border-l-4 border-[#00FFC9] border border-[#2D7544] px-4 py-3 flex items-center gap-2 text-sm">
            <Loader2 size={14} className="animate-spin" />
            <span className="font-mono text-xs uppercase tracking-widest">Claude is thinking…</span>
          </div>
        )}
      </div>

      <div className="border-t border-[#2D7544] bg-[#050805] p-4 pr-44">
        <div className="flex gap-2 items-end">
          <textarea
            data-testid="ai-input"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={onKey}
            rows={2}
            placeholder={isScript ? "e.g. Generate a Python script that parses the spreadsheet…" : "e.g. Summarize the contract and list risks"}
            className="dv-input flex-1 resize-none min-h-[64px]"
          />
          <button data-testid="send-btn" onClick={send} disabled={sending || !input.trim()} className="dv-btn-primary h-[64px] px-5 flex items-center gap-2">
            {sending ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
            Send
          </button>
        </div>
      </div>
    </div>
  );
}

function findPrevUserPrompt(messages, idx) {
  for (let i = idx - 1; i >= 0; i--) {
    if (messages[i].role === "user") return messages[i].content;
  }
  return null;
}

function MessageBubble({ m, saveContext }) {
  const isUser = m.role === "user";
  if (isUser) {
    return (
      <div className="bg-[#050805] border border-[#2D7544] text-[#00FF41] p-4 max-w-[85%] self-end font-medium" data-testid="msg-user">
        <div className="dv-overline mb-1">You</div>
        <div className="whitespace-pre-wrap text-sm leading-relaxed">{m.content}</div>
      </div>
    );
  }
  return (
    <div className="bg-[#050805] border border-[#2D7544] border-l-4 border-l-[#00FFC9] p-4 max-w-[92%] self-start" data-testid="msg-assistant">
      <div className="dv-overline mb-2">Claude · Sonnet 4.5</div>
      <RichMarkdown text={m.content} language={m.language} saveContext={saveContext} />
    </div>
  );
}

// Minimal markdown-ish renderer with code block extraction
function RichMarkdown({ text, language, saveContext }) {
  const segments = [];
  const regex = /```([a-zA-Z0-9_+-]*)\n([\s\S]*?)```/g;
  let last = 0;
  let match;
  while ((match = regex.exec(text)) !== null) {
    if (match.index > last) segments.push({ type: "text", value: text.slice(last, match.index) });
    segments.push({ type: "code", lang: match[1] || language || "code", value: match[2] });
    last = regex.lastIndex;
  }
  if (last < text.length) segments.push({ type: "text", value: text.slice(last) });
  if (segments.length === 0) segments.push({ type: "text", value: text });

  return (
    <div className="flex flex-col gap-3">
      {segments.map((s, i) =>
        s.type === "code" ? (
          <CodeBlock key={`code-${i}-${(s.value||'').slice(0,16)}`} lang={s.lang} code={s.value} saveContext={saveContext} />
        ) : (
          <Prose key={`prose-${i}-${(s.value||'').slice(0,16)}`} text={s.value} />
        )
      )}
    </div>
  );
}

function Prose({ text }) {
  // Simple inline formatting: bold **x**, headings #, bullets -
  const lines = text.split("\n");
  return (
    <div className="text-sm leading-relaxed text-[#00FF41]">
      {lines.map((ln, i) => {
        if (/^#{1,3}\s/.test(ln)) {
          const level = ln.match(/^#+/)[0].length;
          const content = ln.replace(/^#+\s/, "");
          const cls = level === 1 ? "font-heading text-xl font-extrabold tracking-tighter mt-2" : level === 2 ? "font-heading text-lg font-bold tracking-tight mt-2" : "font-bold mt-1";
          return <div key={`h-${i}-${content.slice(0,16)}`} className={cls}>{renderInline(content)}</div>;
        }
        if (/^\s*[-*]\s+/.test(ln)) {
          return <div key={`b-${i}-${ln.slice(0,16)}`} className="flex gap-2"><span className="text-[#00FFC9] font-bold">▸</span><span>{renderInline(ln.replace(/^\s*[-*]\s+/, ""))}</span></div>;
        }
        if (ln.trim() === "") return <div key={`blank-${i}`} className="h-2" />;
        return <div key={`l-${i}-${ln.slice(0,16)}`}>{renderInline(ln)}</div>;
      })}
    </div>
  );
}

function renderInline(s) {
  const parts = s.split(/(\*\*[^*]+\*\*|`[^`]+`)/g);
  return parts.map((p, i) => {
    if (/^\*\*[^*]+\*\*$/.test(p)) return <strong key={`s-${i}-${p.slice(0,12)}`} className="font-bold">{p.slice(2, -2)}</strong>;
    if (/^`[^`]+`$/.test(p)) return <code key={`c-${i}-${p.slice(0,12)}`} className="font-mono text-[12px] px-1 bg-[#0D1F12] border border-[#2D7544]">{p.slice(1, -1)}</code>;
    return <span key={`t-${i}-${(p||'').slice(0,12)}`}>{p}</span>;
  });
}

function CodeBlock({ lang, code, saveContext }) {
  const copy = () => {
    navigator.clipboard.writeText(code).then(() => toast.success("Copied"));
  };
  const download = () => {
    const ext = extOfLang(lang);
    const blob = new Blob([code], { type: "text/plain" });
    downloadBlob(blob, `script.${ext}`);
  };
  const save = async () => {
    const defaultName =
      (saveContext?.prompt || "").slice(0, 50).trim() ||
      `Script ${new Date().toLocaleString()}`;
    const name = await promptDialog({
      title: "Save script to library",
      message: "Give this script a name. You can iterate on it later in the Library tab.",
      defaultValue: defaultName,
      confirmLabel: "Save",
    });
    if (!name || !name.trim()) return;
    try {
      await api.createScript(saveContext.projectId, {
        name: name.trim(),
        language: lang || "auto",
        code,
        prompt: saveContext.prompt || null,
        document_ids: saveContext.documentIds || [],
      });
      toast.success(`Saved "${name.trim()}" to Library`);
      saveContext.onSaved?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    }
  };
  return (
    <div className="border border-[#2D7544] bg-[#050805] text-[#00FF41] font-mono text-xs overflow-hidden" data-testid="code-block">
      <div className="flex justify-between items-center px-3 py-2 border-b border-[#1F4D2A] text-[#66B07A] text-[10px] uppercase tracking-widest">
        <span>{lang || "code"}</span>
        <div className="flex items-center gap-1">
          {saveContext && (
            <button onClick={save} className="px-2 py-1 hover:bg-[#0D1F12] flex items-center gap-1 text-[#00FFC9]" data-testid="save-to-library-btn">
              <BookmarkPlus size={11} /> Save to library
            </button>
          )}
          <button onClick={copy} className="px-2 py-1 hover:bg-[#0D1F12] flex items-center gap-1" data-testid="copy-code-btn">
            <Copy size={11} /> Copy
          </button>
          <button onClick={download} className="px-2 py-1 hover:bg-[#0D1F12] flex items-center gap-1" data-testid="download-code-btn">
            <Download size={11} /> Download
          </button>
        </div>
      </div>
      <pre className="p-4 overflow-x-auto whitespace-pre"><code>{code}</code></pre>
    </div>
  );
}

function extOfLang(l) {
  const x = (l || "").toLowerCase();
  return {
    python: "py", javascript: "js", typescript: "ts", bash: "sh", shell: "sh",
    sql: "sql", go: "go", rust: "rs", java: "java", powershell: "ps1", json: "json", yaml: "yaml", yml: "yml",
  }[x] || "txt";
}
